import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";

const getDisplayName = (user) => {
  if (user?.first_name || user?.last_name) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }
  return user?.full_name || user?.email || "";
};

export default function TimeEntryForm({ entry, onSubmit, onClose, isLoading }) {
  const [formData, setFormData] = useState({
    user_email: "",
    project_id: "",
    task_id: "",
    date: "",
    start_time: "",
    end_time: "",
    hours: "",
    description: "",
    related_to_type: "none",
    related_to_id: "",
    billable: true,
  });

  const { data: user } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () => base44.auth.me(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () => base44.entities.Project.list(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: tasks = [] } = useQuery({
    queryKey: ["tasks"],
    queryFn: () => base44.entities.Task.list(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: investors = [] } = useQuery({
    queryKey: ["investors"],
    queryFn: () => base44.entities.Investor.list(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: () => base44.entities.BusinessCustomer.list(),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (entry) {
      const startDate = entry.start_time ? new Date(entry.start_time) : new Date();
      setFormData({
        user_email: entry.user_email || "",
        project_id: entry.project_id || "",
        task_id: entry.task_id || "",
        date: entry.start_time ? startDate.toISOString().split('T')[0] : "",
        start_time: entry.start_time ? startDate.toTimeString().slice(0, 5) : "",
        end_time: entry.end_time ? new Date(entry.end_time).toTimeString().slice(0, 5) : "",
        hours: entry.duration_minutes ? (entry.duration_minutes / 60).toString() : "",
        description: entry.description || "",
        related_to_type: entry.related_to_type || "none",
        related_to_id: entry.related_to_id || "",
        billable: entry.billable !== undefined ? entry.billable : true,
      });
    } else {
      setFormData({
        ...formData,
        user_email: user?.email || "",
        date: new Date().toISOString().split('T')[0],
      });
    }
  }, [entry, user]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    let durationMinutes;
    if (formData.hours) {
      durationMinutes = parseFloat(formData.hours) * 60;
    } else if (formData.start_time && formData.end_time) {
      const start = new Date(`${formData.date}T${formData.start_time}`);
      const end = new Date(`${formData.date}T${formData.end_time}`);
      durationMinutes = (end - start) / 60000;
    } else {
      alert("Vennligst oppgi enten timer eller start/slutt tidspunkt");
      return;
    }

    const startDateTime = formData.start_time 
      ? new Date(`${formData.date}T${formData.start_time}`).toISOString()
      : new Date(formData.date).toISOString();

    const endDateTime = formData.end_time && formData.start_time
      ? new Date(`${formData.date}T${formData.end_time}`).toISOString()
      : new Date(new Date(startDateTime).getTime() + durationMinutes * 60000).toISOString();

    const project = projects.find(p => p.id === formData.project_id);
    const task = tasks.find(t => t.id === formData.task_id);
    const selectedUser = users.find(u => u.email === formData.user_email);
    
    let relatedName = null;
    if (formData.related_to_type === "investor") {
      const investor = investors.find(i => i.id === formData.related_to_id);
      relatedName = investor?.name;
    } else if (formData.related_to_type === "customer") {
      const customer = customers.find(c => c.id === formData.related_to_id);
      relatedName = customer?.company_name;
    }

    const data = {
      user_email: formData.user_email || user?.email,
      user_name: selectedUser ? getDisplayName(selectedUser) : getDisplayName(user),
      project_id: formData.project_id || null,
      project_name: project?.name || null,
      task_id: formData.task_id || null,
      task_title: task?.title || null,
      start_time: startDateTime,
      end_time: endDateTime,
      duration_minutes: Math.round(durationMinutes),
      description: formData.description,
      related_to_type: formData.related_to_type,
      related_to_id: formData.related_to_type !== "none" && formData.related_to_type !== "internal" ? formData.related_to_id : null,
      related_to_name: relatedName,
      is_running: false,
      billable: formData.billable,
    };

    onSubmit(data);
  };

  const filteredTasks = formData.project_id 
    ? tasks.filter(t => t.project_id === formData.project_id)
    : tasks;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-xl font-semibold text-slate-900">
            {entry ? "Rediger Tidsregistrering" : "Registrer Tid Manuelt"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {user?.role === "admin" && (
            <div className="space-y-2">
              <Label htmlFor="user_email">Bruker *</Label>
              <Select 
                value={formData.user_email} 
                onValueChange={(v) => setFormData({ ...formData, user_email: v })}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Velg bruker" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.email}>
                      {getDisplayName(u)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="date">Dato *</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hours">Timer</Label>
              <Input
                id="hours"
                type="number"
                step="0.25"
                placeholder="f.eks. 2.5"
                onWheel={(e) => e.target.blur()}
                className="h-11 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                value={formData.hours}
                onChange={(e) => setFormData({ ...formData, hours: e.target.value, start_time: "", end_time: "" })}
              />
            </div>
          </div>

          <div className="text-center text-sm text-slate-500">eller</div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="start_time">Start Tid</Label>
              <Input
                id="start_time"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value, hours: "" })}
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="end_time">Slutt Tid</Label>
              <Input
                id="end_time"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value, hours: "" })}
                className="h-11"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="project_id">Prosjekt</Label>
              <Select 
                value={formData.project_id} 
                onValueChange={(v) => {
                const selectedProject = projects.find(p => p.id === v);
                setFormData(prev => ({
                  ...prev,
                  project_id: v,
                  task_id: "",
                  related_to_type: selectedProject?.related_to_type || prev.related_to_type,
                  related_to_id: selectedProject?.related_to_id || prev.related_to_id,
                }));
              }}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Velg prosjekt" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={null}>Ingen prosjekt</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="task_id">Oppgave</Label>
              <Select 
                value={formData.task_id} 
                onValueChange={(v) => setFormData({ ...formData, task_id: v })}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Velg oppgave" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={null}>Ingen oppgave</SelectItem>
                  {filteredTasks.map((task) => (
                    <SelectItem key={task.id} value={task.id}>
                      {task.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="related_to_type">Relatert Til</Label>
              <Select 
                value={formData.related_to_type} 
                onValueChange={(v) => setFormData({ ...formData, related_to_type: v, related_to_id: "" })}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ingen</SelectItem>
                  <SelectItem value="internal">Internt</SelectItem>
                  <SelectItem value="investor">Investor</SelectItem>
                  <SelectItem value="customer">Kunde</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.related_to_type === "investor" && (
              <div className="space-y-2">
                <Label htmlFor="related_to_id">Velg Investor</Label>
                <Select 
                  value={formData.related_to_id} 
                  onValueChange={(v) => setFormData({ ...formData, related_to_id: v })}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Velg investor" />
                  </SelectTrigger>
                  <SelectContent>
                    {investors.map((investor) => (
                      <SelectItem key={investor.id} value={investor.id}>
                        {investor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.related_to_type === "customer" && (
              <div className="space-y-2">
                <Label htmlFor="related_to_id">Velg Kunde</Label>
                <Select 
                  value={formData.related_to_id} 
                  onValueChange={(v) => setFormData({ ...formData, related_to_id: v })}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Velg kunde" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beskrivelse</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Hva jobbet du med?"
            />
          </div>

          <div className="flex items-center gap-2 p-4 bg-slate-50 rounded-lg">
            <Checkbox 
              id="billable"
              checked={formData.billable}
              onCheckedChange={(checked) => setFormData({ ...formData, billable: checked })}
            />
            <Label htmlFor="billable" className="cursor-pointer">
              Fakturerbar tid
            </Label>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Avbryt</Button>
            <Button type="submit" disabled={isLoading} className="bg-slate-900 hover:bg-slate-800">
              {isLoading ? "Lagrer..." : entry ? "Oppdater" : "Registrer Tid"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}